"use client";

import { decimalPlaces } from "@/lib/utils";
import { Star, StarHalf } from "lucide-react";
import { useState } from "react";

interface RatingDisplayProps {
    rating: number | null;
    ratingCount?: number | null;
}

export default function RatingDisplay({
    rating,
    ratingCount,
}: RatingDisplayProps) {
    const formattedRating = decimalPlaces(rating, 2);
    const formattedRatingCount =
        ratingCount && ratingCount > 0 ? `(${Math.round(ratingCount)})` : null;

    return (
        <span className="flex flex-wrap items-center gap-1">
            <RatingStars fill={rating ?? 0} />
            <span className="font-medium">{formattedRating}</span>
            <span className="text-muted-foreground">/5</span>
            {formattedRatingCount && (
                <span className="text-muted-foreground">
                    {formattedRatingCount}
                </span>
            )}
        </span>
    );
}

function starKind(fill: number, index: number): "full" | "half" | "empty" {
    const remainder = fill - index;
    if (remainder > 0.5) return "full";
    if (remainder > 0) return "half";
    return "empty";
}

function StarIcon({ kind }: { kind: "full" | "half" | "empty" }) {
    if (kind === "full") {
        return <Star className="text-rating size-4" fill="currentColor" />;
    }

    if (kind === "half") {
        return (
            <span className="relative inline-flex size-4">
                <Star className="text-rating size-4" />
                <StarHalf
                    className="text-rating absolute inset-0 size-4"
                    fill="currentColor"
                />
            </span>
        );
    }

    return <Star className="text-rating size-4" />;
}

export function RatingStars({
    fill,
    onClick,
    disabled,
}: {
    fill: number;
    onClick?: (rating: number) => void;
    disabled?: boolean;
}) {
    const [hoverRating, setHoverRating] = useState<number | null>(null);

    if (fill < 0 || fill > 5) {
        console.error(`rating ${fill} is invalid`);
        return null;
    }

    const preview = onClick ? (hoverRating ?? fill) : fill;

    return (
        <span
            className="flex"
            aria-hidden={!onClick}
            onMouseLeave={() => onClick && setHoverRating(null)}
        >
            {Array.from({ length: 5 }).map((_, index) => {
                const value = index + 1;
                const star = <StarIcon kind={starKind(preview, index)} />;

                if (!onClick) {
                    return (
                        <span key={index} className="inline-flex">
                            {star}
                        </span>
                    );
                }

                return (
                    <button
                        key={index}
                        className="size-5"
                        disabled={disabled}
                        onMouseEnter={() => !disabled && setHoverRating(value)}
                        onClick={() => onClick(value)}
                    >
                        {star}
                    </button>
                );
            })}
        </span>
    );
}
